<template>
  <div class="api-tokens-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          API Token
        </h1>
        <v-btn
          color="primary"
          class="mank-btn"
          elevation="0"
          @click="createToken">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create
        </v-btn>
      </div>

      <div class="main-content">
        <div class="table-area table-area--pad">
          <div class="empty-state text-center" v-if="!hasTokens">
            <div class="empty-state-icon mb-4">
              <v-icon size="80" color="grey-lighten-2">
                mdi-key
              </v-icon>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No API Token Created
              </h3>
              <p class="text-body-3 text--secondary">
                Create a new API token by clicking the button below.
              </p>
            </div>
            <v-btn
              color="primary"
              class="mank-btn"
              elevation="0"
              @click="createToken">
              <Icon name="plus" variant="solid" color="onPrimary"/>
              Create
            </v-btn>
          </div>

          <div v-else class="tokens-table">
            <div class="table-controls mb-6">
              <v-data-table
                :items-per-page="selectedItemsPerPage"
                :page="page"
                :headers="headers"
                :items="tokens"
                :loading="loading"
                class="tokens-data-table"
                item-value="id"
                no-data-text="No API tokens found"
                loading-text="Loading..."
                items-per-page-text="Per page:"
                hide-default-footer
                @update:page="page = $event"
                @update:items-per-page="selectedItemsPerPage = $event">
                <template v-slot:header.name="{ header }">
                  {{ header.text.toUpperCase() }}
                </template>
                <template v-slot:item.name="{ item }">
                  <div class="text-element-2 font-weight-medium">
                    {{ item.name || '—' }}
                  </div>
                </template>
                <template v-slot:item.description="{ item }">
                  <div class="text-element-2 text--secondary">
                    {{ item.description || '—' }}
                  </div>
                </template>
                <template v-slot:item.token="{ item }">
                  <div class="d-flex align-center gap-2">
                    <span class="token-display text-element-2">
                      {{ getTokenDisplay(item) }}
                    </span>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      class="icon-btn"
                      :disabled="!getStoredToken(item.id)"
                      :title="getStoredToken(item.id) ? (tokenVisible[item.id] ? 'Hide' : 'Show') : 'Token is only visible when first created'"
                      @click="toggleTokenVisibility(item.id)">
                      <v-icon size="18">
                        {{ tokenVisible[item.id] ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
                      </v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      class="icon-btn"
                      :disabled="!getCopyableToken(item)"
                      :title="getCopyableToken(item) ? 'Copy token' : 'Token is only visible when first created'"
                      @click="copyToken(item)">
                      <v-icon size="18">
                        mdi-content-copy
                      </v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-slot:item.expires_at="{ item }">
                  <div class="text-element-2">
                    {{ formatDate(item.expires_at) }}
                  </div>
                </template>
                <template v-slot:item.created_at="{ item }">
                  <div class="text-element-2">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    class="delete-btn mank-btn-icon"
                    title="Delete"
                    @click="confirmDelete(item)">
                    <Icon name="trash" variant="solid" color="default" size="16"/>
                  </v-btn>
                </template>
              </v-data-table>
              <div class="table-footer">
                <v-pagination
                  v-model="page"
                  :total-visible="6"
                  :length="totalPages"/>
                <div class="pagination-info ml-auto mr-0">
                  <span class="text-caption-1 text--secondary">Showing</span>
                  <v-select
                    class="items-per-page-select"
                    :items="itemsPerPage"
                    v-model="selectedItemsPerPage"
                    hide-details
                    outlined>
                    <template v-slot:append>
                      <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                    </template>
                  </v-select>
                  <span class="text-caption-1 text--secondary">/ {{ totalItems }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDeleteSubmit"/>
  </div>
</template>

<script>
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';
import Icon from '../components/Icon.vue';
import ApiTokenService from '../services/ApiTokenService';

const STORAGE_PREFIX = 'api_token_';

export default {
  name: 'ApiTokens',
  components: {
    DeleteConfirmationModal,
    Icon,
  },
  data() {
    return {
      loading: false,
      page: 1,
      selectedItemsPerPage: 5,
      itemsPerPage: [5, 10, 20, 50, 100],
      showDeleteDialog: false,
      tokenToDelete: null,
      tokenVisible: {},
      headers: [
        { text: 'Name', align: 'start', value: 'name', sortable: false, width: '20%' },
        { text: 'Description', align: 'start', value: 'description', sortable: false, width: '30%' },
        { text: 'Token', align: 'start', value: 'token', sortable: false, width: '25%' },
        { text: 'Expiration date', align: 'start', value: 'expires_at', sortable: false, width: '15%' },
        { text: 'Date Created', align: 'start', value: 'created_at', sortable: false, width: '15%' },
        { text: '', align: 'end', value: 'actions', sortable: false, width: '5%' },
      ],
      tokens: [],
    };
  },
  computed: {
    hasTokens() {
      return this.tokens.length > 0;
    },
    totalItems() {
      return this.tokens.length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.selectedItemsPerPage));
    },
  },
  async mounted() {
    await this.fetchTokens();
  },
  methods: {
    getStoredToken(id) {
      try {
        return sessionStorage.getItem(STORAGE_PREFIX + id) || null;
      } catch {
        return null;
      }
    },
    getTokenDisplay(item) {
      const stored = this.getStoredToken(item.id);
      if (stored && this.tokenVisible[item.id]) {
        return stored;
      }
      return '••••••••';
    },
    getCopyableToken(item) {
      const stored = this.getStoredToken(item.id);
      if (stored) return stored;
      return null;
    },
    toggleTokenVisibility(id) {
      const stored = this.getStoredToken(id);
      if (!stored) {
        this.$store.dispatch('ui/showNotification', {
          type: 'info',
          title: 'Info',
          message: 'Token is only visible when first created.',
        });
        return;
      }
      this.$set(this.tokenVisible, id, !this.tokenVisible[id]);
    },
    async copyToken(item) {
      const toCopy = this.getCopyableToken(item);
      if (!toCopy) {
        this.$store.dispatch('ui/showNotification', {
          type: 'info',
          title: 'Info',
          message: 'Token is only visible when first created.',
        });
        return;
      }
      try {
        await navigator.clipboard.writeText(toCopy);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Token copied to clipboard.',
        });
      } catch {
        const ta = document.createElement('textarea');
        ta.value = toCopy;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Token copied to clipboard.',
        });
      }
    },
    async fetchTokens() {
      this.loading = true;
      try {
        const response = await ApiTokenService.getList();
        if (response && response.success && Array.isArray(response.data)) {
          this.tokens = response.data.map((t) => ({
            id: t.id,
            name: t.name ?? '',
            description: t.description ?? '',
            expires_at: t.expires_at ?? null,
            created_at: t.created_at,
          }));
        } else {
          this.tokens = [];
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.response?.data?.message || error.message || 'Failed to load API tokens.',
        });
        this.tokens = [];
      } finally {
        this.loading = false;
      }
    },
    createToken() {
      this.$router.push({ name: 'CreateApiToken' });
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString('tr-TR');
    },
    confirmDelete(item) {
      this.tokenToDelete = item;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.tokenToDelete = null;
    },
    async confirmDeleteSubmit() {
      if (!this.tokenToDelete) {
        this.showDeleteDialog = false;
        return;
      }
      try {
        const response = await ApiTokenService.delete(this.tokenToDelete.id);
        if (response && response.success !== false) {
          try {
            sessionStorage.removeItem(STORAGE_PREFIX + this.tokenToDelete.id);
          } catch (_) {}
          const idx = this.tokens.findIndex((t) => t.id === this.tokenToDelete.id);
          if (idx !== -1) this.tokens.splice(idx, 1);
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: 'API token deleted.',
          });
        } else {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: response?.message || 'Failed to delete token.',
          });
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.response?.data?.message || error.message || 'Failed to delete token.',
        });
      }
      this.showDeleteDialog = false;
      this.tokenToDelete = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 400px;
  margin: 0 auto;

  .empty-state-icon {
    width: 80px;
    height: 80px;
    background: $light-surface-1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }
}

.table-controls {
  display: flex;
  flex-direction: column;
}

.token-display {
  font-family: monospace;
  letter-spacing: 0.02em;
}

.icon-btn {
  color: $light-foreground-2;
  &:hover:not(:disabled) {
    color: $light-foreground-1;
    background: rgba($light-foreground-2, 0.1);
  }
}

.tokens-data-table {
  background: $light-surface-2;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;

  :deep(.v-table) {
    background: $light-surface-2;
  }
  :deep(.v-data-table__thead) {
    background: $light-surface-1;
  }
  :deep(.v-data-table-header__content) {
    font-weight: 600;
    color: $light-foreground-2;
    font-size: $body-3-size;
  }
  :deep(.v-data-table__tr:hover) {
    background: rgba($primary-500, 0.02) !important;
  }
}

.delete-btn {
  color: $light-foreground-2;
  &:hover {
    color: $error-500;
    background: rgba($error-500, 0.1);
  }
}

.table-footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  border-top: 1px solid $light-border;
  height: 48px;
}

.pagination-info {
  font-size: $caption-1-size;
  color: $light-foreground-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.items-per-page-select {
  width: 48px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
}

::v-deep .v-data-table-header {
  background-color: $light-surface-1;
}
::v-deep .v-data-table-header tr th {
  color: $light-foreground-3 !important;
  font-weight: 600;
  font-size: $body-3-size !important;
  text-transform: capitalize;
}
::v-deep .mank-btn-icon {
  padding: 4px;
  border-radius: 8px;
  background: $light-surface-1;
  width: 24px;
  height: 24px;
}
</style>
